// src/app/services/cognition-service.ts
import { apiFetch } from "@/api/api-fetch";

// ========================================================================
// TYPE DEFINITIONS (Backend-aligned + tolerant)
// ========================================================================

export type ProviderId = "openai" | "claude" | "perplexity" | "gemini";

export interface BrandSnapshot {
  available: boolean;
  tier?: string | null; // e.g. "Premium"
  strengths?: string[];
  weaknesses?: string[];
  lastUpdated?: string | null; // ISO optional
}

export interface AutoExtractedProduct {
  id: string;
  name: string;
  matchScore: number; // 0-100
  category?: string | null;
  year?: string | null;
  type?: string | null;
  trim?: string | null;
}

export interface CognitionCandidatesResponse {
  brandName: string;
  autoExtractedProducts: AutoExtractedProduct[];
  brandSnapshot?: BrandSnapshot | null;
}

export interface TaskSetup {
  model: ProviderId | string;
  category: string;
  persona: string;
  taskType: string;
  confidence: "High" | "Moderate" | "Low" | string;
}

export interface RetrievalStrategyRow {
  strategy: string;
  values: Record<string, boolean | string>;
}

export interface RetrievalOrderItem {
  model: ProviderId | string;
  order: string[];
}

export interface DomainInfluence {
  domain: string;
  influence: number; // 0-10
  models: number;
  category: string;
  citations: number;
}

export interface AttributeComparisonRow {
  attribute: string;
  values: Record<string, string>;
  consensus: string; // Strong | Moderate | Weak | Divergent
}

export interface BaselineSummary {
  overallScore: number; // 0-100
  topDomain: { name: string; influence: number };
  avgCompleteness: number;
  strongConsensus: number;
  totalAttributes: number;
  topStrength: string;
  keyWeakness: string;

  // Optional: if backend returns a prebuilt narrative summary
  runSummary?: string | null;
  summary?: string | null;
}

export interface CompetitiveComparison {
  product: string;
  // backend might send overallScore OR overall
  overallScore?: number;
  overall?: number;

  performance: "High" | "Moderate" | "Low" | string;
  value: "High" | "Moderate" | "Low" | string;
  features: "High" | "Moderate" | "Low" | string;
}

export interface RankingFactorsItem {
  model: ProviderId | string;
  performance: number; // 0-100
  value: number;       // 0-100
  brand: number;       // 0-100
  availability: number; // 0-100
  reviews: number;     // 0-100
  usability: number;   // 0-100
  durability: number;  // 0-100
  innovation: number;  // 0-100
}

export interface ResponseConstruction {
  model: ProviderId | string;
  confidence: number; // 0-100
  // Backend (ResponseConstructionItem) sends a single strength/weakness/narrative
  // per model — not pros/cons arrays. Field names must match the payload or the
  // per-model synthesis cards render blank.
  topStrength: string;
  keyWeakness: string;
  narrative: string;
}

export interface Gap {
  attribute: string;
  gapReason: string;
  modelsAffected: number;
  severity: "High" | "Moderate" | "Low" | "high" | "medium" | "low" | string;
  impact: string;
}

export interface CognitiveStageMetric {
  label: string;
  value: string;
  context: string;
}

export type StageTitle =
  | "How Did AI Frame Your Product Search?"
  | "Where Did AI Look For Information & What Did It Find?"
  | "How Is AI Comparing You?"
  | "What Is AI Telling People?";

export type StageId =
  | "task-setup"
  | "evidence-signals"
  | "understanding-decisions"
  | "final-synthesis";

export interface StageBlurb {
  headline: string;
  metricsLine: string;
  body: string;
}

export interface SectionHelpCopy {
  explainThis: string;
  whyDoesThisMatter: string;
}

export interface CognitiveStage {
  id: StageId;
  title: StageTitle;
  description: string;
  stageNumber: string;
  narrativeHook: string;
  metrics: CognitiveStageMetric[];
  whyThisMatters: string[];
  accentColor: string;

  // NEW
  blurb?: StageBlurb | null;
  helpCopy?: Record<string, SectionHelpCopy> | null;
  modelBehaviorInsight?: string | null;
  keyTakeaway?: string | null;
  keyInsights?: string[] | null;
}

export interface AttributeVarianceItem {
  attribute: string;
  uniqueValues: number;
  modelsWithValue: number;
  values: Record<string, string>;
}

export interface RankingLogicItem {
  model: string;
  rankPosition?: number | null;
  relativeScore?: number | null;
  factorWeights?: Record<string, number>; // 0-100
  topDrivers?: string[];
  tradeoffs?: string[];
  whyRankedHere?: string | null;
}

export interface CognitionHistoryItem {
  id: number;
  runId?: number;
  productName: string;
  modelCount: number;
  timestamp: string;
  baselineSnapshot?: string;
  selectedModels?: string[];
  /** run's overall perception score, 0-100 (null if the run produced none). */
  overallScore?: number | null;
  /** same score normalized to 0-10 (null if none). */
  overallScore10?: number | null;
}

export interface CognitionRunData {
  runId?: number;
  baselineSnapshot?: string;

  brandName: string;
  productId: string;
  productName: string;
  selectedModels: string[];

  taskSetupData: TaskSetup[];
  retrievalStrategiesData: RetrievalStrategyRow[];
  retrievalOrderData: RetrievalOrderItem[];
  domainData: DomainInfluence[];
  attributeData: AttributeComparisonRow[];
  baselineSummaryData: BaselineSummary;
  competitiveComparisonData: CompetitiveComparison[];
  rankingFactorsData: RankingFactorsItem[];
  responseConstructionData: ResponseConstruction[];
  gapsData: Gap[];
  cognitiveStages: CognitiveStage[];

  // NEW
  attributeVarianceData?: AttributeVarianceItem[];
  rankingLogicData?: RankingLogicItem[];

  // Optional: if backend sends a top-level runSummary string
  runSummary?: string | null;
}

// ========================================================================
// API FUNCTIONS
// ========================================================================

export async function fetchCognitionHistory(brand: string): Promise<CognitionHistoryItem[]> {
  try {
    const json = await apiFetch<{ history: CognitionHistoryItem[] }>(
      `/cognition/history?brand=${encodeURIComponent(brand)}`
    );
    return json.history || [];
  } catch (e) {
    console.error("Failed to fetch history", e);
    return [];
  }
}

export async function fetchCognitionRun(runId: number): Promise<CognitionRunData> {
  return await apiFetch<CognitionRunData>(`/cognition/run/${runId}`);
}
